import { CardMode, CardRecord, CardType, SrsFeedback } from '../db/indexedDb';

const dayMs = 24 * 60 * 60 * 1000;

export type TimeRounding = 'exact' | 'day';

export interface SkillConfig {
  initialInterval: number;
  initialEase: number;
  easeAdjust: { again: number; hard: number; good: number; min: number; max: number };
  intervalAdjust: { hard: number; good: number };
  restartAfterDays: number;
  minIntervalMinutes: number;
  rounding: TimeRounding;
  maxIntervalDays: number;
  relearn: { startBucket: number; exitStreak: number; resetInterval: number; maxBucket?: number };
  priority: { relearn: number; review: number; overdueFactor: number; easePenalty: number };
}

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const defaultSkillConfigs: Record<CardType, SkillConfig> = {
  speaking: {
    initialInterval: 0.5,
    initialEase: 2.3,
    easeAdjust: { again: -0.3, hard: -0.15, good: 0.1, min: 1.3, max: 3.2 },
    intervalAdjust: { hard: 1.15, good: 2.1 },
    restartAfterDays: 7,
    minIntervalMinutes: 45,
    rounding: 'day',
    maxIntervalDays: 120,
    relearn: { startBucket: 1, exitStreak: 2, resetInterval: 0.25, maxBucket: 3 },
    priority: { relearn: 200, review: 120, overdueFactor: 12, easePenalty: 8 },
  },
  writing: {
    initialInterval: 1,
    initialEase: 2.2,
    easeAdjust: { again: -0.25, hard: -0.12, good: 0.08, min: 1.3, max: 3.3 },
    intervalAdjust: { hard: 1.1, good: 1.9 },
    restartAfterDays: 10,
    minIntervalMinutes: 90,
    rounding: 'day',
    maxIntervalDays: 180,
    relearn: { startBucket: 1, exitStreak: 2, resetInterval: 0.5, maxBucket: 3 },
    priority: { relearn: 190, review: 110, overdueFactor: 10, easePenalty: 8 },
  },
  listening: {
    initialInterval: 0.4,
    initialEase: 2.4,
    easeAdjust: { again: -0.2, hard: -0.1, good: 0.09, min: 1.3, max: 3.4 },
    intervalAdjust: { hard: 1.12, good: 2.15 },
    restartAfterDays: 5,
    minIntervalMinutes: 30,
    rounding: 'day',
    maxIntervalDays: 90,
    relearn: { startBucket: 1, exitStreak: 2, resetInterval: 0.25, maxBucket: 3 },
    priority: { relearn: 180, review: 105, overdueFactor: 9, easePenalty: 6 },
  },
  vocab: {
    initialInterval: 0.3,
    initialEase: 2.5,
    easeAdjust: { again: -0.15, hard: -0.08, good: 0.1, min: 1.3, max: 3.5 },
    intervalAdjust: { hard: 1.2, good: 2.4 },
    restartAfterDays: 4,
    minIntervalMinutes: 20,
    rounding: 'exact',
    maxIntervalDays: 60,
    relearn: { startBucket: 1, exitStreak: 3, resetInterval: 0.2, maxBucket: 3 },
    priority: { relearn: 170, review: 100, overdueFactor: 8, easePenalty: 5 },
  },
};

export interface SrsResult {
  interval: number;
  ease: number;
  nextReviewDate: number;
  consecutiveGood: number;
  mode: CardMode;
  isRelearn: boolean;
  relearnBucket?: number;
  priority: number;
  effectiveConfig: SkillConfig;
}

export const endOfDay = (timestamp: number) => {
  const dt = new Date(timestamp);
  dt.setHours(23, 59, 59, 999);
  return dt.getTime();
};

const applyTimeRounding = (intervalDays: number, now: number, config: SkillConfig) => {
  const minIntervalMs = config.minIntervalMinutes * 60 * 1000;
  const targetMs = Math.max(intervalDays * dayMs, minIntervalMs);
  const candidate = now + targetMs;

  if (config.rounding === 'exact') {
    return candidate;
  }

  const todayEnd = endOfDay(now);
  if (targetMs < dayMs) {
    return Math.min(candidate, todayEnd);
  }

  const days = Math.max(1, Math.ceil(targetMs / dayMs));
  return endOfDay(now + days * dayMs);
};

const computePriority = (mode: CardMode, ease: number, nextReviewDate: number, now: number, config: SkillConfig) => {
  const overdueDays = Math.max(0, Math.floor((now - nextReviewDate) / dayMs));
  const base = mode === 'relearn' ? config.priority.relearn : config.priority.review;
  return base + overdueDays * config.priority.overdueFactor + Math.max(0, config.priority.easePenalty - ease * 2);
};

export function getSkillConfig(type: CardType, override?: Partial<SkillConfig>): SkillConfig {
  const base = defaultSkillConfigs[type];
  return {
    ...base,
    easeAdjust: { ...base.easeAdjust, ...(override?.easeAdjust ?? {}) },
    intervalAdjust: { ...base.intervalAdjust, ...(override?.intervalAdjust ?? {}) },
    relearn: { ...base.relearn, ...(override?.relearn ?? {}) },
    priority: { ...base.priority, ...(override?.priority ?? {}) },
    initialInterval: override?.initialInterval ?? base.initialInterval,
    initialEase: override?.initialEase ?? base.initialEase,
    restartAfterDays: override?.restartAfterDays ?? base.restartAfterDays,
    minIntervalMinutes: override?.minIntervalMinutes ?? base.minIntervalMinutes,
    rounding: override?.rounding ?? base.rounding,
    maxIntervalDays: override?.maxIntervalDays ?? base.maxIntervalDays,
  };
}

export function computeNextReview(
  card: Pick<CardRecord, 'type' | 'mode' | 'interval' | 'ease' | 'consecutiveGood' | 'relearnBucket' | 'isRelearn' | 'nextReviewDate'>,
  feedback: SrsFeedback,
  now: number = Date.now(),
  overrideConfig?: Partial<SkillConfig>
): SrsResult {
  const config = getSkillConfig(card.type, overrideConfig);
  const previousInterval = Math.max(0, card.interval ?? 0);
  const previousEase = card.ease ?? config.initialEase;
  const previousStreak = card.consecutiveGood ?? 0;
  const wasRelearn = card.isRelearn ?? card.mode === 'relearn';
  const restart = previousInterval >= config.restartAfterDays && (feedback === 'again' || feedback === 'hard');

  let ease = previousEase;
  let interval = previousInterval;
  let mode: CardMode = card.mode ?? 'review';
  let consecutiveGood = feedback === 'good' ? previousStreak + 1 : 0;
  let relearnBucket = card.relearnBucket ?? config.relearn.startBucket;
  let isRelearn = wasRelearn;

  if (feedback === 'again') {
    ease = clamp(ease + config.easeAdjust.again, config.easeAdjust.min, config.easeAdjust.max);
    interval = config.relearn.resetInterval;
    mode = 'relearn';
    isRelearn = true;
    relearnBucket = config.relearn.startBucket;
    consecutiveGood = 0;
  } else if (feedback === 'hard') {
    ease = clamp(ease + config.easeAdjust.hard, config.easeAdjust.min, config.easeAdjust.max);
    interval = Math.max(config.relearn.resetInterval, previousInterval * config.intervalAdjust.hard);
    mode = 'relearn';
    isRelearn = true;
    consecutiveGood = 0;
    if (restart) {
      relearnBucket = config.relearn.startBucket;
    }
  } else {
    ease = clamp(ease + config.easeAdjust.good, config.easeAdjust.min, config.easeAdjust.max);
    interval = previousInterval > 0 ? previousInterval * config.intervalAdjust.good : config.initialInterval;

    if (isRelearn) {
      const maxBucket = config.relearn.maxBucket ?? config.relearn.startBucket;
      relearnBucket = Math.min(maxBucket, relearnBucket + 1);
      if (consecutiveGood >= config.relearn.exitStreak) {
        isRelearn = false;
        mode = 'review';
        relearnBucket = undefined;
      }
    } else {
      isRelearn = false;
      mode = 'review';
    }
  }

  interval = Math.min(config.maxIntervalDays, Math.max(interval, config.relearn.resetInterval));
  const nextReviewDate = applyTimeRounding(interval, now, config);
  const priority = computePriority(mode, ease, card.nextReviewDate ?? now, now, config);

  return {
    interval,
    ease,
    nextReviewDate,
    consecutiveGood,
    mode,
    isRelearn,
    relearnBucket,
    priority,
    effectiveConfig: config,
  };
}
